/**
 * Un numero de serie corrigeable, avec son journal (qui, quand, pourquoi).
 *
 * Sorti de `pages/DistributorClientDetail.jsx` le 26/10 (lot L1, fichier > 500 lignes),
 * sans changement de comportement.
 */
import { useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { toast } from 'sonner';
import { messageDeRefus } from '@/features/distributeur/refus';

export default function ChampNumeroSerie({ label, value, champ, assetId, onUpdated, tid }) {
  const [editing, setEditing] = useState(false);
  const [nouveau, setNouveau] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [journal, setJournal] = useState([]);
  const openEdit = () => { setNouveau(value || ''); setMotif(''); setEditing(true); };
  const save = async () => {
    if (!nouveau.trim()) { toast.error('Nouveau N° requis'); return; }
    setSaving(true);
    try {
      await api.put(`/distributeur/assets/${assetId}/serial`, { nouveau_numero: nouveau.trim(), champ, motif });
      toast.success('N° série mis à jour + journal enregistré');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(messageDeRefus(e, 'Erreur'));
    }
    setSaving(false);
  };
  const openJournal = async () => {
    try {
      const r = await api.get(`/distributeur/assets/${assetId}/journal`);
      setJournal(r.data.journal || []);
      setShowJournal(true);
    } catch { toast.error('Erreur chargement journal'); }
  };
  return (
    <div data-testid={tid}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wide text-neutral-400 font-medium">{label}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={openJournal} className="text-[9px] text-neutral-400 hover:text-neutral-700" title="Voir le journal" data-testid={`${tid}-journal-btn`}>
            📖
          </button>
          <button type="button" onClick={openEdit} className="text-[9px] text-blue-500 hover:text-blue-700 font-medium underline" data-testid={`${tid}-edit-btn`}>
            Modifier
          </button>
        </div>
      </div>
      <p className="text-neutral-800 font-mono text-[11.5px]" data-testid={`${tid}-value`}>{value || '—'}</p>
      {editing && (
        <div className="mt-2 p-2 rounded-md bg-blue-50/50 border border-blue-100 space-y-2" data-testid={`${tid}-modal`}>
          <Input value={nouveau} onChange={(e) => setNouveau(e.target.value)} placeholder="Nouveau N° série" className="h-8 text-[12px]" data-testid={`${tid}-input`} />
          <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (changement de produit…)" className="h-8 text-[11px]" data-testid={`${tid}-motif`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="h-7 text-[11px]" data-testid={`${tid}-save`}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-[11px]">Annuler</Button>
          </div>
        </div>
      )}
      {showJournal && (
        <div className="mt-2 p-2 rounded-md bg-neutral-50 border border-neutral-200" data-testid={`${tid}-journal-panel`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Journal modifications</p>
            <button type="button" onClick={() => setShowJournal(false)} className="text-[10px] text-neutral-400 hover:text-neutral-700">Fermer</button>
          </div>
          {journal.length === 0 ? (
            <p className="text-[10.5px] text-neutral-400 italic">Aucune modification enregistrée.</p>
          ) : (
            <ul className="space-y-1.5">
              {journal.filter(j => j.champ === champ).map((j, idx) => (
                <li key={idx} className="text-[10.5px] text-neutral-700 border-l-2 border-blue-300 pl-2">
                  <span className="font-mono text-red-600 line-through">{j.ancien_numero || '(vide)'}</span>
                  {' → '}
                  <span className="font-mono text-emerald-700 font-semibold">{j.nouveau_numero}</span>
                  <div className="text-neutral-500">
                    {new Date(j.date).toLocaleString('fr-FR')} · {j.user_nom} ({j.user_role})
                    {j.motif && <> — <em>{j.motif}</em></>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
